/**
 * Auth Context - Provides authentication state and permission checking
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/auth.service';

interface User {
  id: number;
  username: string;
  email: string;
  is_superuser: boolean;
  permissions: string[];
  rol: string | null;
  voornaam?: string;
  achternaam?: string;
  volledige_naam?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  hasPermission: (permission: string | string[]) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  isAdmin: () => boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Force update component when user changes
  const [, forceUpdate] = useState({});
  
  // Clean up debug logging
  // useEffect(() => {
  //   console.log('AuthContext: User changed:', user);
  //   if (user) {
  //     console.log('AuthContext: User permissions:', user.permissions);
  //     console.log('AuthContext: User rol:', user.rol);
  //     console.log('AuthContext: User is_superuser:', user.is_superuser);
  //   }
  // }, [user]);

  const loadUser = async () => {
    if (!authService.isAuthenticated()) {
      setLoading(false);
      return;
    }

    try {
      const userData = await authService.getCurrentUser();
      setUser(userData);
      // Force re-render of all components using this context
      forceUpdate({});
      // Small delay to ensure UI has time to react to user change
      await new Promise(resolve => setTimeout(resolve, 100));
    } catch (err) {
      console.error('Failed to load user:', err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUser();
    
    // Listen for storage changes (token updates from other tabs/windows)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'access_token') {
        if (e.newValue) {
          loadUser();
        } else {
          setUser(null);
          setLoading(false);
        }
      }
    };

    // Listen for token changes in same tab
    const handleTokenChange = (e: CustomEvent) => {
      if (e.detail.token) {
        loadUser();
      } else {
        setUser(null);
        setLoading(false);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('tokenChanged', handleTokenChange as EventListener);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('tokenChanged', handleTokenChange as EventListener);
    };
  }, []);

  /**
   * Check if user has a specific permission or any of multiple permissions
   * @param permission - Single permission string or array of permissions (any must match)
   */
  const hasPermission = (permission: string | string[]): boolean => {
    if (!user) return false;
    if (user.is_superuser) return true;
    if (user.permissions.includes('*')) return true;

    if (Array.isArray(permission)) {
      return permission.some(p => user.permissions.includes(p));
    }
    return user.permissions.includes(permission);
  };

  /**
   * Check if user has ANY of the provided permissions
   */
  const hasAnyPermission = (permissions: string[]): boolean => {
    if (!user) return false;
    if (user.is_superuser) return true;
    if (user.permissions.includes('*')) return true;

    return permissions.some(p => user.permissions.includes(p));
  };

  /**
   * Check if user has ALL of the provided permissions
   */
  const hasAllPermissions = (permissions: string[]): boolean => {
    if (!user) return false;
    if (user.is_superuser) return true;
    if (user.permissions.includes('*')) return true;

    return permissions.every(p => user.permissions.includes(p));
  };

  /**
   * Check if user is admin (superuser or has Admin role)
   */
  const isAdmin = (): boolean => {
    if (!user) return false;
    return user.is_superuser || user.rol === 'Admin';
  };

  /**
   * Refresh user data from API
   */
  const refreshUser = async () => {
    await loadUser();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        isAdmin,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to use auth context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Hook for permission-based conditional rendering
 */
export const usePermission = (permission: string | string[]): boolean => {
  const { hasPermission } = useAuth();
  return hasPermission(permission);
};

export default AuthContext;
